import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  LayoutDashboard,
  User,
  Users,
  Bell,
  CalendarDays,
  MessageSquareWarning,
  WalletCards,
  UserRoundCheck,
  Car,
  Building2,
  FileText,
  BriefcaseBusiness,
  Settings,
  X,
  QrCode
} from "lucide-react";
import logo from "../../assets/Sankrutilogo.jpeg";
import api from "../../api/axios";
import styles from "./ResidentSidebar.module.css";

const ResidentSidebar = ({
  mobileOpen = false,
  onClose,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [user, setUser] = useState({ fullName: 'Resident', flatNumber: '', wing: '', role: 'Resident' });

  const [unreadCount, setUnreadCount] = useState(0);

  useEffect(() => {
    const userInfo = JSON.parse(localStorage.getItem('userInfo') || '{}');
    if (userInfo && userInfo.fullName) {
      setUser(userInfo);
    }
    
    // Fetch notifications to get unread count
    const fetchNotificationsCount = async () => {
      try {
        const { data } = await api.get('/users/notifications');
        const count = data.filter(n => !n.read).length;
        setUnreadCount(count);
      } catch (error) {
        console.error('Failed to fetch notifications for sidebar', error);
      }
    };
    
    fetchNotificationsCount();
    
    window.addEventListener('notificationUpdated', fetchNotificationsCount);
    return () => window.removeEventListener('notificationUpdated', fetchNotificationsCount);
  }, []);

  const initials = user.fullName ? user.fullName.split(" ").map((n) => n[0]).join("").substring(0, 2).toUpperCase() : "R";

  const residentMenuGroups = [
    {
      title: "MAIN",
      items: [
        { label: "Dashboard", path: "/resident", icon: LayoutDashboard },
        { label: "My Gate Pass", path: "/resident/gate-pass", icon: QrCode },
      ]
    },
    {
      title: "COMMUNITY",
      items: [
        { label: "Notices", path: "/resident/notices", icon: Bell },
        { label: "Events", path: "/resident/events", icon: CalendarDays },
        { label: "Polls & Voting", path: "/resident/polls", icon: FileText },
      ]
    },
    {
      title: "SERVICES",
      items: [
        { label: "Complaints", path: "/resident/complaints", icon: MessageSquareWarning },
        { label: "Maintenance & Payments", path: "/resident/maintenance", icon: WalletCards },
        { label: "Visitors", path: "/resident/visitors", icon: UserRoundCheck },
        { label: "Vehicles", path: "/resident/vehicles", icon: Car },
        { label: "Amenities & Bookings", path: "/resident/amenities", icon: Building2 },
      ]
    },
    {
      title: "RESOURCES",
      items: [
        { label: "Documents", path: "/resident/documents", icon: FileText },
        { label: "Notifications", path: "/resident/notifications", icon: Bell, badge: unreadCount > 0 ? unreadCount : null },
      ]
    },
    {
      title: "ACCOUNT",
      items: [
        { label: "My Profile", path: "/resident/profile", icon: User },
        { label: "My Family", path: "/resident/family", icon: Users },
        { label: "My Business", path: "/resident/business", icon: BriefcaseBusiness },
        { label: "Settings", path: "/resident/settings", icon: Settings },
      ]
    }
  ];

  const handleNavigate = async (item) => {
    if (item.path.includes('/notifications') && unreadCount > 0) {
      try {
        await api.put('/users/notifications/read');
        setUnreadCount(0);
        window.dispatchEvent(new Event('notificationUpdated'));
      } catch (e) {
        console.error(e);
      }
    }
    navigate(item.path);

    if (onClose) {
      onClose();
    }
  };

  return (
    <>
      {/* Mobile Overlay */}
      {mobileOpen && <div className={styles.overlay} onClick={onClose} />}

      <aside className={`${styles.sidebar} ${mobileOpen ? styles.open : ""}`}>
        {/* Mobile Close */}
        <button
          className={styles.closeButton}
          onClick={onClose}
          aria-label="Close sidebar"
        >
          <X size={20} />
        </button>

        {/* ================= LOGO ================= */}
        <div className={styles.logoArea} onClick={() => navigate('/')} style={{ cursor: 'pointer' }} title="Go to Website Home">
          <img src={logo} alt="Sanskriti Logo" className={styles.logoImage} />
        </div>


        {/* ================= MENU ================= */}
        <nav className={styles.navigation}>
          {residentMenuGroups.map((group, index) => (
            <div key={index} className={styles.navGroup}>
              {group.title && <div className={styles.groupTitle}>{group.title}</div>}
              {group.items.map((item) => {
                const Icon = item.icon;
                const isActive = location.pathname === item.path;

                return (
                  <button
                    key={item.label}
                    className={`${styles.navItem} ${isActive ? styles.active : ""}`}
                    onClick={() => handleNavigate(item)}
                  >
                    <Icon className={styles.navIcon} size={16} strokeWidth={1.7} />

                    <span>{item.label}</span>

                    {item.badge && (
                      <span className={styles.badge}>{item.badge}</span>
                    )}
                  </button>
                );
              })}
            </div>
          ))}
        </nav>

        {/* ================= BOTTOM ================= */}
        <div style={{ marginTop: 'auto' }}>
          <button
            className={styles.navItem}
            onClick={() => handleNavigate({ path: "/resident/emergency" })}
            style={{ color: '#ef4444', backgroundColor: 'rgba(239, 68, 68, 0.05)', fontWeight: 600, border: '1px solid rgba(239, 68, 68, 0.2)' }}
          >
            <MessageSquareWarning className={styles.navIcon} size={16} strokeWidth={2} />
            <span>Emergency Contacts</span>
          </button>
        </div>
      </aside>
    </>
  );
};

export default ResidentSidebar;
